import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase, signInWithProvider } from '../../lib/auth';
import { parseAuthCallback, authCallbackErrorMessage, type AuthCallback } from '../../lib/authCallback';

// Page d'activation d'un compte invité. Deux façons d'activer :
//  1) SSO Google / Microsoft (recommandé, aucun mot de passe à gérer)
//  2) définir un mot de passe (repli)
// Le jeton d'invitation ouvre d'abord une session ; ensuite l'utilisateur choisit.
// Cadre de page défini HORS du composant : défini dedans, il serait recréé à chaque rendu et
// React démonterait/remonterait le formulaire à chaque frappe (perte du focus dans le champ).
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="refonte-scope min-h-screen flex flex-col justify-center px-4 py-10 bg-cream text-ink">
    <div className="w-full max-w-[420px] mx-auto">
      <div className="text-center mb-8">
        <img src="/hook-logo.png" alt="Hook" className="h-16 w-auto mx-auto" />
      </div>
      <div className="bg-panel border border-line rounded-card p-6 sm:p-8 shadow-sm">{children}</div>
    </div>
  </div>
);

const InvitePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState<'google' | 'azure' | null>(null);
  const [isVerifyingToken, setIsVerifyingToken] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [showPwForm, setShowPwForm] = useState(false);
  // Lien au format token_hash : le jeton n'est consommé qu'au clic (résiste aux scanners d'emails).
  const [pendingTokenHash, setPendingTokenHash] = useState<Extract<AuthCallback, { kind: 'token_hash' }> | null>(null);

  useEffect(() => {
    let done = false;
    const ok = () => { if (!done) { done = true; setTokenValid(true); setError(null); setIsVerifyingToken(false); } };
    const fail = (msg: string) => { if (!done) { done = true; setError(msg); setTokenValid(false); setIsVerifyingToken(false); } };
    // Retire les jetons de l'URL une fois lus (pas de re-traitement au refresh, pas de fuite dans l'historique).
    const cleanUrl = () => { try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ } };

    // Filet : si la lib pose la session de son côté (code PKCE…), on en profite.
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => { if (session?.user) ok(); });

    const verify = async () => {
      setIsVerifyingToken(true); setError(null);
      try {
        const cb = parseAuthCallback(window.location.hash, window.location.search, 'invite');

        // 1) Lien d'invitation standard : jetons dans le hash (flux implicite). Le client est en PKCE,
        //    la lib refuse ce hash → on pose la session nous-mêmes. C'ÉTAIT le bug « lien invalide ».
        if (cb.kind === 'implicit') {
          const { data, error } = await supabase.auth.setSession({ access_token: cb.access_token, refresh_token: cb.refresh_token });
          cleanUrl();
          if (!error && data.session?.user) { ok(); return; }
          fail(error?.message || "Impossible d'ouvrir la session depuis ce lien.");
          return;
        }

        // 2) Supabase a renvoyé une erreur (jeton déjà consommé, expiré…).
        if (cb.kind === 'error') { cleanUrl(); fail(authCallbackErrorMessage(cb)); return; }

        // 3) Lien token_hash (template email personnalisé) : on attend le clic de l'utilisateur.
        if (cb.kind === 'token_hash') {
          setPendingTokenHash(cb);
          setIsVerifyingToken(false);
          return;
        }

        // 4) Session déjà établie (lien déjà traité, utilisateur revenu sur la page).
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) { ok(); return; }

        // 5) Ancien format : token + email en query.
        const token = searchParams.get('token')?.trim();
        const email = searchParams.get('email')?.trim();
        if (token && email) {
          const { data, error } = await supabase.auth.verifyOtp({ token, type: 'invite', email });
          if (!error && data.session) { ok(); return; }
        }

        // 6) Laisse une chance au traitement asynchrone avant de conclure.
        setTimeout(async () => {
          if (done) return;
          const { data: { session: s2 } } = await supabase.auth.getSession();
          if (s2?.user) ok();
          else fail("Lien d'invitation invalide ou expiré. Demande un nouveau lien.");
        }, 1200);
      } catch {
        fail("Une erreur s'est produite lors de la vérification du lien.");
      }
    };
    verify();
    return () => { sub.subscription.unsubscribe(); };
  }, [searchParams]);

  // Format token_hash : consommation du jeton au clic seulement.
  const activateFromTokenHash = async () => {
    if (!pendingTokenHash) return;
    setIsVerifyingToken(true); setError(null);
    try {
      const { data, error } = await supabase.auth.verifyOtp({ token_hash: pendingTokenHash.token_hash, type: pendingTokenHash.type as any });
      try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ }
      if (error || !data.session) {
        setError(error?.message?.match(/expired|invalid/i)
          ? 'Ce lien a déjà été utilisé ou a expiré. Demande un nouveau lien à Hook.'
          : (error?.message || 'Lien invalide.'));
        setTokenValid(false);
      } else { setTokenValid(true); setPendingTokenHash(null); }
    } catch (e: any) { setError(e?.message || 'Erreur de vérification.'); setTokenValid(false); }
    setIsVerifyingToken(false);
  };

  const validatePassword = (pwd: string) => {
    if (pwd.length < 8) return 'Le mot de passe doit contenir au moins 8 caractères.';
    if (!/(?=.*[a-z])/.test(pwd)) return 'Il faut au moins une minuscule.';
    if (!/(?=.*[A-Z])/.test(pwd)) return 'Il faut au moins une majuscule.';
    if (!/(?=.*\d)/.test(pwd)) return 'Il faut au moins un chiffre.';
    return null;
  };

  const onSso = async (provider: 'google' | 'azure') => {
    setError(null); setSsoLoading(provider);
    try {
      // L'utilisateur a une session (jeton d'invitation) ; l'OAuth sur le MÊME email
      // rattache le provider au compte existant, puis retour sur /app.
      await signInWithProvider(provider);
    } catch (err: any) {
      const msg = String(err?.message || '');
      setError(msg.includes('not enabled') || msg.includes('provider')
        ? 'Connexion SSO pas encore activée côté serveur — préviens Hook.'
        : (msg || 'Erreur SSO'));
      setSsoLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null);
    if (!tokenValid) { setError("Session invalide. Utilise un nouveau lien."); return; }
    const pErr = validatePassword(password);
    if (pErr) { setError(pErr); return; }
    if (password !== confirmPassword) { setError('Les mots de passe ne correspondent pas.'); return; }
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Session expirée. Utilise un nouveau lien.');
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setSuccess(true);
      setTimeout(() => navigate('/app'), 1500);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la définition du mot de passe.');
    } finally { setIsLoading(false); }
  };

  if (isVerifyingToken) return (
    <Shell><div className="text-center py-4">
      <Loader2 className="h-10 w-10 text-hook-rose animate-spin mx-auto mb-4" />
      <h2 className="text-[17px] font-bold">Vérification du lien…</h2>
    </div></Shell>
  );

  if (success) return (
    <Shell><div className="text-center py-4">
      <CheckCircle className="h-12 w-12 text-st-ok mx-auto mb-3" />
      <h2 className="text-[17px] font-bold mb-1">Compte activé !</h2>
      <p className="text-dim text-[13px]">Redirection vers ton espace…</p>
    </div></Shell>
  );

  if (pendingTokenHash && !tokenValid && !error) return (
    <Shell><div className="text-center py-4">
      <h2 className="text-[20px] font-extrabold mb-1">Bienvenue dans l'univers Hook 👋</h2>
      <p className="text-dim text-[13px] mb-5">Clique pour activer ton compte, puis choisis comment te connecter.</p>
      <button onClick={activateFromTokenHash} className="w-full text-[13.5px] font-semibold px-4 py-2.5 rounded-lg bg-hook-rose text-white hover:opacity-90">Activer mon compte</button>
    </div></Shell>
  );

  if (!tokenValid) return (
    <Shell><div className="text-center py-4">
      <AlertCircle className="h-12 w-12 text-st-bad mx-auto mb-3" />
      <h2 className="text-[17px] font-bold mb-2">Lien d'invitation invalide</h2>
      <p className="text-dim text-[13px] mb-5">{error || 'Le lien est invalide ou a expiré.'}</p>
      <button onClick={() => navigate('/login')} className="w-full text-[13px] font-semibold px-4 py-2.5 rounded-lg bg-hook-rose text-white hover:opacity-90">Retour à la connexion</button>
    </div></Shell>
  );

  return (
    <Shell>
      <h2 className="text-center text-[20px] font-extrabold mb-1">Bienvenue dans l'univers Hook 👋</h2>
      <p className="text-center text-dim text-[13px] mb-6">Active ton compte pour accéder à ton espace.</p>

      {error && (
        <div className="mb-4 text-[12.5px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />{error}
        </div>
      )}

      {/* Microsoft masqué (Hook = Google Workspace, pas de tenant Azure). */}
      <div>
        <button onClick={() => onSso('google')} disabled={!!ssoLoading}
          className="w-full flex items-center justify-center gap-2.5 text-[13.5px] font-semibold px-4 py-2.5 rounded-lg border border-line-2 bg-panel hover:border-ink disabled:opacity-50">
          {ssoLoading === 'google' ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="font-bold text-[15px]">G</span>}
          Continuer avec Google
        </button>
      </div>

      <div className="flex items-center gap-3 my-5">
        <span className="h-px bg-line flex-1" /><span className="font-mono text-[10px] uppercase tracking-widest text-faint">ou</span><span className="h-px bg-line flex-1" />
      </div>

      {!showPwForm ? (
        <button onClick={() => setShowPwForm(true)} className="w-full text-[13px] font-semibold text-dim hover:text-ink underline">
          Définir un mot de passe à la place
        </button>
      ) : (
        <form className="space-y-3" onSubmit={handleSubmit}>
          <div className="relative">
            <input type={showPassword ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Mot de passe (8+ car., Maj/min/chiffre)"
              className="w-full rounded-lg border border-line-2 bg-panel px-3 py-2.5 pr-10 text-[14px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hook-rose" />
            <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-faint">
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <input type={showPassword ? 'text' : 'password'} required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
            placeholder="Confirme le mot de passe"
            className="w-full rounded-lg border border-line-2 bg-panel px-3 py-2.5 text-[14px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hook-rose" />
          <button type="submit" disabled={isLoading || !password || !confirmPassword}
            className="w-full text-[13.5px] font-semibold px-4 py-2.5 rounded-lg bg-hook-rose text-white hover:opacity-90 disabled:opacity-50">
            {isLoading ? 'Activation…' : 'Activer avec ce mot de passe'}
          </button>
        </form>
      )}
    </Shell>
  );
};

export default InvitePage;
